export type ComprehensionQuestion = {
  question: string;
  options: string[];
  correctIndex: number;
  explanation?: string;
};

export type ComprehensionText = {
  id: string;
  language: "es" | "pt" | "fi";
  level: "A1" | "A2" | "B1" | "B2";
  title: string;
  text: string;
  wordCount: number;
  questions: ComprehensionQuestion[];
};

export const comprehensionTexts: ComprehensionText[] = [
  // ─── SPANISH A1 ───────────────────────────────────────────────────────────
  {
    id: "es-a1-familia",
    language: "es",
    level: "A1",
    title: "Mi Familia",
    wordCount: 68,
    text: `Me llamo Ana. Tengo treinta años y vivo en Madrid con mi familia. Mi marido se llama Carlos y tiene treinta y dos años. Tenemos dos hijos: una niña de cinco años y un niño de ocho años. Mi niña se llama Sofía y mi niño se llama Miguel. Los fines de semana vamos al parque juntos. Me gusta mucho mi familia.`,
    questions: [
      {
        question: "¿Cómo se llama el marido de Ana?",
        options: ["Pedro", "Carlos", "Miguel", "Luis"],
        correctIndex: 1,
      },
      {
        question: "¿Cuántos hijos tiene Ana?",
        options: ["Uno", "Tres", "Dos", "Ninguno"],
        correctIndex: 2,
      },
      {
        question: "¿Dónde vive Ana?",
        options: ["Barcelona", "Sevilla", "Valencia", "Madrid"],
        correctIndex: 3,
      },
      {
        question: "¿Qué hacen los fines de semana?",
        options: ["Van al cine", "Comen en restaurantes", "Van al parque", "Viajan"],
        correctIndex: 2,
      },
    ],
  },
  {
    id: "es-a1-desayuno",
    language: "es",
    level: "A1",
    title: "El Desayuno",
    wordCount: 72,
    text: `Todos los días me levanto a las siete de la mañana. Primero me ducho y luego desayuno. Para el desayuno como tostadas con mantequilla y bebo café con leche. También como una fruta. Después de desayunar, me visto y salgo de casa. Tomo el metro para ir al trabajo. Llego a la oficina a las nueve.`,
    questions: [
      {
        question: "¿A qué hora se levanta?",
        options: ["Las seis", "Las ocho", "Las siete", "Las nueve"],
        correctIndex: 2,
      },
      {
        question: "¿Qué bebe en el desayuno?",
        options: ["Té", "Zumo de naranja", "Agua", "Café con leche"],
        correctIndex: 3,
      },
      {
        question: "¿Cómo va al trabajo?",
        options: ["En coche", "En bicicleta", "En metro", "A pie"],
        correctIndex: 2,
      },
      {
        question: "¿A qué hora llega a la oficina?",
        options: ["Las ocho", "Las nueve", "Las diez", "Las once"],
        correctIndex: 1,
      },
    ],
  },

  // ─── SPANISH A2 ───────────────────────────────────────────────────────────
  {
    id: "es-a2-mercado",
    language: "es",
    level: "A2",
    title: "El Fin de Semana Pasado",
    wordCount: 85,
    text: `El sábado pasado fui al mercado con mi madre. Compramos verduras, fruta y queso. Después, fuimos a un café y tomamos chocolate caliente. Por la tarde, visité a mi amiga Carmen. Ella vive cerca de mi casa. Jugamos a las cartas y escuchamos música. Fue un día muy agradable. El domingo me quedé en casa porque llovió todo el día.`,
    questions: [
      {
        question: "¿Con quién fue al mercado?",
        options: ["Con su padre", "Con su amiga Carmen", "Con su madre", "Con su hermano"],
        correctIndex: 2,
      },
      {
        question: "¿Qué compraron en el mercado?",
        options: ["Carne y pescado", "Pan y leche", "Verduras, fruta y queso", "Ropa y zapatos"],
        correctIndex: 2,
      },
      {
        question: "¿Qué hizo por la tarde del sábado?",
        options: ["Fue al cine", "Estudió en casa", "Visitó a Carmen", "Hizo deporte"],
        correctIndex: 2,
      },
      {
        question: "¿Por qué se quedó en casa el domingo?",
        options: ["Estaba enfermo/a", "Tenía mucho trabajo", "Estaba cansado/a", "Llovió todo el día"],
        correctIndex: 3,
      },
    ],
  },
  {
    id: "es-a2-barcelona",
    language: "es",
    level: "A2",
    title: "El Viaje a Barcelona",
    wordCount: 91,
    text: `El mes pasado viajé a Barcelona con unos amigos. Viajamos en tren desde Madrid. El viaje duró tres horas. En Barcelona visitamos la Sagrada Familia y el Parque Güell. La Sagrada Familia me pareció impresionante. Por las noches salimos a cenar tapas en el barrio gótico. La comida estuvo deliciosa. También fuimos a la playa un día. Me gustó mucho Barcelona y quiero volver.`,
    questions: [
      {
        question: "¿Cómo viajaron a Barcelona?",
        options: ["En avión", "En coche", "En autobús", "En tren"],
        correctIndex: 3,
      },
      {
        question: "¿Cuánto tiempo duró el viaje en tren?",
        options: ["Una hora", "Dos horas", "Tres horas", "Cuatro horas"],
        correctIndex: 2,
      },
      {
        question: "¿Cómo le pareció la Sagrada Familia?",
        options: ["Aburrida", "Pequeña", "Normal", "Impresionante"],
        correctIndex: 3,
      },
      {
        question: "¿Qué hicieron por las noches?",
        options: ["Fueron al teatro", "Descansaron en el hotel", "Salieron a cenar tapas", "Fueron a bailar"],
        correctIndex: 2,
      },
    ],
  },

  // ─── SPANISH B1 ───────────────────────────────────────────────────────────
  {
    id: "es-b1-teletrabajo",
    language: "es",
    level: "B1",
    title: "El Teletrabajo",
    wordCount: 112,
    text: `Desde la pandemia, muchas personas trabajan desde casa. Yo llevo dos años trabajando en remoto y tengo sentimientos encontrados. Por un lado, me ahorro el tiempo del transporte y puedo organizar mejor mi día. Además, trabajo más concentrado porque hay menos interrupciones. Por otro lado, echo de menos el contacto con mis compañeros. A veces me siento solo y me cuesta separar el trabajo de la vida personal. Creo que lo ideal sería trabajar desde casa dos o tres días a la semana y ir a la oficina el resto del tiempo.`,
    questions: [
      {
        question: "¿Cuánto tiempo lleva el autor trabajando en remoto?",
        options: ["Un año", "Tres años", "Cuatro años", "Dos años"],
        correctIndex: 3,
      },
      {
        question: "¿Cuál es una ventaja del trabajo remoto según el texto?",
        options: ["Mejor salario", "Más vacaciones", "Menos interrupciones", "Más reuniones"],
        correctIndex: 2,
      },
      {
        question: "¿Qué echa de menos el autor?",
        options: ["Su oficina", "Los cafés cercanos", "Sus compañeros", "Los restaurantes"],
        correctIndex: 2,
      },
      {
        question: "¿Cuál sería la situación ideal según el autor?",
        options: [
          "Siempre trabajar desde casa",
          "Nunca trabajar desde casa",
          "Cambiar completamente de trabajo",
          "Trabajar desde casa algunos días",
        ],
        correctIndex: 3,
      },
    ],
  },
  {
    id: "es-b1-dieta",
    language: "es",
    level: "B1",
    title: "La Dieta Mediterránea",
    wordCount: 120,
    text: `La dieta mediterránea se considera una de las más saludables del mundo. Esta dieta se basa principalmente en verduras, frutas, legumbres, cereales integrales y aceite de oliva. También incluye pescado y marisco regularmente, y cantidades moderadas de carne. Los estudios científicos demuestran que seguir esta dieta reduce el riesgo de enfermedades del corazón y puede alargar la vida. Sin embargo, en los últimos años, muchos jóvenes en los países mediterráneos han abandonado esta tradición en favor de la comida rápida. Es una tendencia preocupante que afecta no solo a la salud individual, sino también a la cultura gastronómica.`,
    questions: [
      {
        question: "¿En qué se basa principalmente la dieta mediterránea?",
        options: [
          "Carne y lácteos",
          "Verduras, frutas y aceite de oliva",
          "Pan y arroz blanco",
          "Comida rápida",
        ],
        correctIndex: 1,
      },
      {
        question: "¿Qué efecto tiene en el corazón según los estudios?",
        options: ["Aumenta el riesgo", "Lo daña con el tiempo", "Reduce el riesgo", "No tiene ningún efecto"],
        correctIndex: 2,
      },
      {
        question: "¿Qué han abandonado muchos jóvenes mediterráneos?",
        options: ["El ejercicio físico", "La siesta", "La dieta mediterránea", "El trabajo"],
        correctIndex: 2,
      },
      {
        question: '¿A qué se refiere el texto como "tendencia preocupante"?',
        options: [
          "El consumo de pescado",
          "El uso de aceite de oliva",
          "El consumo de frutas",
          "La adopción de la comida rápida",
        ],
        correctIndex: 3,
      },
    ],
  },

  // ─── PORTUGUESE A1 ─────────────────────────────────────────────────────────
  {
    id: "pt-a1-rotina",
    language: "pt",
    level: "A1",
    title: "A Minha Rotina",
    wordCount: 73,
    text: `Eu me chamo Pedro. Tenho vinte e cinco anos e moro em Lisboa. Todo dia acordo às sete da manhã. Tomo banho e depois tomo café da manhã. Como pão com manteiga e bebo café. Trabalho num escritório no centro da cidade. Vou ao trabalho de metrô. Chego ao trabalho às nove horas. Almoço perto do escritório. À noite, cozinho em casa e assisto televisão.`,
    questions: [
      {
        question: "Onde mora Pedro?",
        options: ["Porto", "Coimbra", "Lisboa", "Faro"],
        correctIndex: 2,
      },
      {
        question: "O que Pedro come no café da manhã?",
        options: ["Fruta e iogurte", "Ovos e bacon", "Cereais", "Pão com manteiga"],
        correctIndex: 3,
      },
      {
        question: "Como Pedro vai ao trabalho?",
        options: ["De carro", "A pé", "De ônibus", "De metrô"],
        correctIndex: 3,
      },
      {
        question: "O que Pedro faz à noite?",
        options: ["Sai com amigos", "Vai à academia", "Lê livros", "Cozinha e assiste televisão"],
        correctIndex: 3,
      },
    ],
  },
  {
    id: "pt-a1-familia",
    language: "pt",
    level: "A1",
    title: "A Família Silva",
    wordCount: 69,
    text: `A família Silva é de São Paulo. O pai se chama Roberto e a mãe se chama Marta. Eles têm três filhos: João, Ana e Lucas. João tem dezoito anos, Ana tem quinze e Lucas tem dez. Roberto trabalha como médico e Marta é professora. Eles moram numa casa grande com jardim. Nos finais de semana, a família gosta de fazer churrasco no jardim.`,
    questions: [
      {
        question: "De onde é a família Silva?",
        options: ["Rio de Janeiro", "Belo Horizonte", "Brasília", "São Paulo"],
        correctIndex: 3,
      },
      {
        question: "Quantos filhos têm Roberto e Marta?",
        options: ["Dois", "Um", "Quatro", "Três"],
        correctIndex: 3,
      },
      {
        question: "O que faz Marta?",
        options: ["Médica", "Advogada", "Professora", "Engenheira"],
        correctIndex: 2,
      },
      {
        question: "O que a família faz nos finais de semana?",
        options: ["Vai à praia", "Visita parentes", "Viaja", "Faz churrasco no jardim"],
        correctIndex: 3,
      },
    ],
  },

  // ─── PORTUGUESE A2 ─────────────────────────────────────────────────────────
  {
    id: "pt-a2-sintra",
    language: "pt",
    level: "A2",
    title: "O Fim de Semana em Sintra",
    wordCount: 95,
    text: `No último fim de semana, fui a Sintra com minha namorada. Acordamos cedo e pegamos o trem em Lisboa. A viagem durou quarenta minutos. Em Sintra, visitamos o Palácio Nacional e subimos até o Palácio da Pena. A vista de lá de cima foi incrível. Almoçamos num restaurante típico e provamos a famosa travessa serrana. Quando voltamos a Lisboa, estávamos muito cansados, mas muito felizes. Sintra é um lugar mágico que todo turista deveria visitar.`,
    questions: [
      {
        question: "Como foram a Sintra?",
        options: ["De carro", "A pé", "De ônibus", "De trem"],
        correctIndex: 3,
      },
      {
        question: "Quanto tempo durou a viagem?",
        options: ["Vinte minutos", "Trinta minutos", "Uma hora", "Quarenta minutos"],
        correctIndex: 3,
      },
      {
        question: "Como foi a vista do Palácio da Pena?",
        options: ["Normal", "Decepcionante", "Incrível", "Entediante"],
        correctIndex: 2,
      },
      {
        question: "Como se sentiram ao voltar para Lisboa?",
        options: [
          "Animados e descansados",
          "Tristes e cansados",
          "Indiferentes",
          "Cansados mas felizes",
        ],
        correctIndex: 3,
      },
    ],
  },
  {
    id: "pt-a2-brasil",
    language: "pt",
    level: "A2",
    title: "As Regiões do Brasil",
    wordCount: 98,
    text: `O Brasil é um país enorme e diverso. No Norte fica a Amazônia, a maior floresta tropical do mundo. No Nordeste, as praias são famosas pela beleza e pelo calor. No Centro-Oeste fica Brasília, a capital federal. No Sudeste ficam São Paulo e Rio de Janeiro, as maiores cidades. No Sul, o clima é mais frio e há muita influência europeia. Os brasileiros são conhecidos pela alegria e hospitalidade. O carnaval é a festa mais famosa do país.`,
    questions: [
      {
        question: "Onde fica a Amazônia?",
        options: ["No Sul", "No Centro-Oeste", "No Nordeste", "No Norte"],
        correctIndex: 3,
      },
      {
        question: "Onde fica a capital federal do Brasil?",
        options: ["No Nordeste", "No Sul", "No Norte", "No Centro-Oeste"],
        correctIndex: 3,
      },
      {
        question: "Como são conhecidos os brasileiros?",
        options: [
          "Sérios e reservados",
          "Frios e distantes",
          "Alegres e hospitaleiros",
          "Trabalhadores e sérios",
        ],
        correctIndex: 2,
      },
      {
        question: "Qual é a festa mais famosa do Brasil?",
        options: ["O Natal", "A Páscoa", "O carnaval", "A virada do ano"],
        correctIndex: 2,
      },
    ],
  },

  // ─── PORTUGUESE B1 ─────────────────────────────────────────────────────────
  {
    id: "pt-b1-voluntariado",
    language: "pt",
    level: "B1",
    title: "O Voluntariado",
    wordCount: 115,
    text: `Nos últimos anos, o voluntariado tem crescido muito no Brasil. Cada vez mais jovens dedicam parte do seu tempo livre a ajudar comunidades carentes. As razões são variadas: alguns querem ganhar experiência profissional, outros buscam um sentido maior na vida, e muitos simplesmente querem contribuir para uma sociedade mais justa. Uma pesquisa recente mostrou que pessoas que praticam voluntariado relatam níveis mais altos de bem-estar e satisfação pessoal. No entanto, para que o voluntariado seja realmente eficaz, é importante que as organizações ofereçam treinamento adequado e que os voluntários se comprometam a longo prazo.`,
    questions: [
      {
        question: "Qual é a tendência mencionada no texto?",
        options: [
          "Diminuição do voluntariado",
          "Fim do voluntariado",
          "Crescimento do voluntariado",
          "Crise nas organizações",
        ],
        correctIndex: 2,
      },
      {
        question: "Por que alguns jovens fazem voluntariado?",
        options: [
          "Para ganhar dinheiro",
          "Para ganhar experiência profissional",
          "Para conhecer pessoas famosas",
          "Para viajar de graça",
        ],
        correctIndex: 1,
      },
      {
        question: "O que mostrou a pesquisa recente?",
        options: [
          "Voluntários trabalham mais horas",
          "Voluntários ganham mais dinheiro",
          "Voluntários têm mais problemas de saúde",
          "Voluntários relatam maior bem-estar",
        ],
        correctIndex: 3,
      },
      {
        question: "O que é necessário para que o voluntariado seja eficaz?",
        options: [
          "Alto financiamento",
          "Muitos voluntários ao mesmo tempo",
          "Treinamento adequado e comprometimento",
          "Tecnologia avançada",
        ],
        correctIndex: 2,
      },
    ],
  },
  {
    id: "pt-b1-tecnologia",
    language: "pt",
    level: "B1",
    title: "Tecnologia e Vida Social",
    wordCount: 118,
    text: `O uso excessivo das redes sociais tem gerado debate entre especialistas. Por um lado, as plataformas digitais permitem que as pessoas se conectem com amigos e familiares em qualquer parte do mundo. Além disso, são uma fonte importante de informação e entretenimento. Por outro lado, estudos indicam que passar muitas horas nas redes sociais pode aumentar sentimentos de ansiedade e solidão, especialmente entre os jovens. Muitos especialistas recomendam limitar o tempo de tela e praticar o que chamam de "desintoxicação digital". A chave, segundo eles, está no equilíbrio: usar a tecnologia de forma consciente, sem deixar que ela domine a vida cotidiana.`,
    questions: [
      {
        question: "Qual é o tema principal do texto?",
        options: [
          "Os benefícios das redes sociais",
          "O impacto do uso excessivo das redes sociais",
          "Como criar uma rede social",
          "A história da internet",
        ],
        correctIndex: 1,
      },
      {
        question: "Qual é uma vantagem das redes sociais mencionada no texto?",
        options: [
          "São gratuitas para todos",
          "Permitem conexão com pessoas ao redor do mundo",
          "Eliminam a solidão completamente",
          "São sempre seguras",
        ],
        correctIndex: 1,
      },
      {
        question: "O que os estudos indicam sobre o uso excessivo das redes?",
        options: [
          "Aumenta a produtividade",
          "Melhora o sono",
          "Pode aumentar ansiedade e solidão",
          "É sempre positivo",
        ],
        correctIndex: 2,
      },
      {
        question: 'O que é a "desintoxicação digital" mencionada no texto?',
        options: [
          "Usar só aplicativos gratuitos",
          "Limitar o tempo nas telas",
          "Deletar todas as redes sociais",
          "Usar apenas um dispositivo",
        ],
        correctIndex: 1,
      },
    ],
  },
  // ─── FINNISH A1 ───────────────────────────────────────────────────────────
  {
    id: "fi-a1-helsinki",
    language: "fi",
    level: "A1",
    title: "Helsinki",
    wordCount: 58,
    text: `Helsinki on Suomen pääkaupunki. Kaupungissa asuu noin 650 000 ihmistä. Helsinki sijaitsee meren rannalla. Kaupungissa on paljon puistoja ja kahviloita. Talvella on kylmää ja lunta. Kesällä aurinko paistaa pitkään. Helsinki on kaunis kaupunki.`,
    questions: [
      {
        question: "Mikä Helsinki on?",
        options: ["Suomen suurin kaupunki", "Suomen pääkaupunki", "Ruotsin pääkaupunki", "Norjan pääkaupunki"],
        correctIndex: 1,
      },
      {
        question: "Missä Helsinki sijaitsee?",
        options: ["Järven rannalla", "Vuorten lähellä", "Meren rannalla", "Metsän keskellä"],
        correctIndex: 2,
      },
      {
        question: "Millainen Helsinki on kesällä?",
        options: ["Kylmä ja pimeä", "Aurinkoinen ja lämmin", "Sateinen", "Tuulinen"],
        correctIndex: 1,
      },
      {
        question: "Kuinka paljon ihmisiä asuu Helsingissä?",
        options: ["Noin 100 000", "Noin 2 miljoonaa", "Noin 650 000", "Noin 50 000"],
        correctIndex: 2,
      },
    ],
  },
  {
    id: "fi-a1-perhe",
    language: "fi",
    level: "A1",
    title: "Minun perheeni",
    wordCount: 62,
    text: `Minun nimeni on Mikko. Asun Tampereella pienen perheen kanssa. Minulla on vaimo ja kaksi lasta. Vaimoni nimi on Aino. Hän on opettaja. Meidän tyttäremme on seitsemän vuotta vanha. Poikamme on kymmenen vuotta vanha. Viikonloppuisin käymme yhdessä järvellä.`,
    questions: [
      {
        question: "Missä Mikko asuu?",
        options: ["Helsingissä", "Oulussa", "Tampereella", "Turussa"],
        correctIndex: 2,
      },
      {
        question: "Mikä on Mikon vaimon ammatti?",
        options: ["Lääkäri", "Opettaja", "Insinööri", "Kokki"],
        correctIndex: 1,
      },
      {
        question: "Kuinka vanha tyttö on?",
        options: ["Viisi vuotta", "Kahdeksan vuotta", "Kymmenen vuotta", "Seitsemän vuotta"],
        correctIndex: 3,
      },
      {
        question: "Missä perhe käy viikonloppuisin?",
        options: ["Kaupungilla", "Järvellä", "Metsässä", "Rannalla"],
        correctIndex: 1,
      },
    ],
  },
  {
    id: "fi-a2-sauna",
    language: "fi",
    level: "A2",
    title: "Suomalainen sauna",
    wordCount: 74,
    text: `Sauna on tärkeä osa suomalaista kulttuuria. Suomessa on noin kolme miljoonaa saunaa. Lähes jokaisella kotitaloudella on oma sauna tai pääsy julkiseen saunaan. Saunassa lämpötila on yleensä 80–100 astetta. Ihmiset heittävät vettä kiville, jolloin syntyy höyryä. Saunan jälkeen on tapana uida tai pulahtaa järveen. Sauna rentouttaa kehoa ja mieltä.`,
    questions: [
      {
        question: "Kuinka monta saunaa Suomessa on?",
        options: ["Noin miljoona", "Noin kolme miljoonaa", "Noin kymmenen tuhatta", "Noin viisi miljoonaa"],
        correctIndex: 1,
      },
      {
        question: "Mikä on tyypillinen lämpötila saunassa?",
        options: ["40–60 astetta", "60–80 astetta", "80–100 astetta", "Yli 120 astetta"],
        correctIndex: 2,
      },
      {
        question: "Mitä tehdään saunan jälkeen?",
        options: ["Syödään", "Uidaan tai pulahdutaan järveen", "Nukutaan", "Juodaan kahvia"],
        correctIndex: 1,
      },
      {
        question: "Miksi sauna on suosittu?",
        options: ["Se on halpa", "Se rentouttaa kehoa ja mieltä", "Se on moderni keksintö", "Se on pakollinen"],
        correctIndex: 1,
      },
    ],
  },
];

export function getTextsForLanguage(language: string): ComprehensionText[] {
  return comprehensionTexts.filter((t) => t.language === language);
}

export function getTextsForLanguageAndLevel(
  language: string,
  level: "A1" | "A2" | "B1" | "B2"
): ComprehensionText[] {
  return comprehensionTexts.filter((t) => t.language === language && t.level === level);
}
